import { loginSchema, type LoginInput } from '@app/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { MarcaEmblema, MosaicoAstral } from '../../../components/branding';
import { LanguageToggle } from '../../../components/i18n/LanguageToggle';
import { ThemeToggle } from '../../../components/theme/ThemeToggle';
import { Alert } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorCode } from '../../../lib/api';
import { qk } from '../../../lib/queryKeys';
import { authApi } from '../api/auth.api';

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const es = useI18n();
  const [authError, setAuthError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({ resolver: zodResolver(loginSchema) });

  const loginMutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: ({ user }) => {
      queryClient.setQueryData(qk.auth.me, user);
      const next = searchParams.get('next') || (user.debeCambiarPassword ? '/cambiar-password' : '/');
      navigate(next, { replace: true });
    },
    onError: (error) => {
      const code = getErrorCode(error);
      if (code === 'ACCOUNT_LOCKED') setAuthError(es.errores.auth.bloqueado);
      else if (code === 'RATE_LIMITED') setAuthError(es.errores.auth.tasaLimite);
      else setAuthError(es.errores.auth.credenciales);
    },
  });

  const onSubmit = (data: LoginInput): void => {
    setAuthError(null);
    loginMutation.mutate(data);
  };

  return (
    <div className="grid h-screen overflow-hidden md:grid-cols-2">
      {/* ---- Hero de marca (solo desktop): mariposa recién salida del capullo ---- */}
      <aside
        className="relative hidden overflow-hidden bg-marca-primario-oscuro bg-cover bg-center md:flex md:flex-col md:justify-end md:p-10"
        style={{ backgroundImage: "url('/branding/login-mariposa.jpg')" }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />

        {/* Título. */}
        <div className="relative z-10">
          <h2 className="font-titulos text-4xl font-bold leading-tight text-white">
            {es.auth.login.heroTitulo}
          </h2>
        </div>
      </aside>

      {/* ---- Formulario ---- */}
      <main className="relative flex items-center justify-center overflow-hidden bg-surface-sunken px-4 py-6">
        {/* Mosaico decorativo: yoga, planetas y astrología, detrás de la tarjeta. */}
        <MosaicoAstral className="pointer-events-none absolute inset-0 z-0 select-none text-black/[0.07] dark:text-white/[0.09]" />

        {/* Toggle de tema (claro/oscuro/sistema) accesible antes de loguear. */}
        <div className="absolute right-3 top-3 z-20 flex items-center gap-1">
          <LanguageToggle />
          <ThemeToggle />
        </div>
        <div className="relative z-10 w-full max-w-md">
          <div className="mb-5 flex flex-col items-center gap-2">
            <MarcaEmblema className="h-14" />
          </div>

          <form
            onSubmit={(e) => void handleSubmit(onSubmit)(e)}
            className="flex flex-col gap-4 rounded-xl border border-border-subtle bg-surface/75 p-6 shadow-card backdrop-blur-sm"
            noValidate
          >
            <h2 className="text-lg font-semibold text-text-strong">{es.auth.login.titulo}</h2>

            {authError && (
              <Alert variant="error" title={es.auth.login.tituloError}>
                {authError}
              </Alert>
            )}

            <div className="flex flex-col gap-2">
              <Label htmlFor="email">{es.auth.login.email}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                aria-invalid={!!errors.email}
                {...register('email')}
              />
              {errors.email && <span className="text-xs text-danger">{errors.email.message}</span>}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="password">{es.auth.login.password}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                aria-invalid={!!errors.password}
                {...register('password')}
              />
              {errors.password && <span className="text-xs text-danger">{errors.password.message}</span>}
            </div>

            <Button type="submit" disabled={isSubmitting || loginMutation.isPending}>
              {isSubmitting || loginMutation.isPending ? es.auth.login.entrando : es.auth.login.entrar}
            </Button>

            <p className="text-center text-xs text-text-faint">
              <Link to="/recuperar-password" className="hover:underline">
                {es.auth.login.olvideMiPassword}
              </Link>
            </p>
          </form>

          <p className="mt-4 text-center text-xs text-text-faint">
            © {new Date().getFullYear()} {es.app.organizacion} · {es.app.equipo}
          </p>
        </div>
      </main>
    </div>
  );
}
